import { CircleAlert, Info, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { WAITLIST_URL } from '@/config/urls';
import type { UnsupportedAccountReason } from '@/services/gmail-forwarding.service';
import { ONBOARDING_COPY } from './onboardingCopy';

/**
 * `account_mismatch` is not an "unsupported account" — the account is fine, it
 * just isn't the one signed in. It shares this card because the layout and
 * placement are identical, but it opts out of the waitlist link below, which
 * would be nonsense advice for someone who already has a personal Gmail.
 */
export type AccountNoticeReason = UnsupportedAccountReason | 'account_mismatch';

export interface UnsupportedAccountNoticeProps {
  reason: AccountNoticeReason;
  email?: string;
  /** The Yaruta session's address. Only used by `account_mismatch`. */
  signedInEmail?: string;
  /** Optional primary CTA (e.g. "Connect a personal Gmail" / re-OAuth). */
  actionLabel?: string;
  onAction?: () => void;
  /**
   * `onboarding` — the Hero's hard-stop card: title, governed copy, no glyph.
   * `connect`    — the Connect Gmail screen's inline error (YAR-160 AC6):
   *                the same governed copy WITH the glyph and NO title.
   * `default`    — Settings, where the longer copy names the two capabilities.
   */
  variant?: 'default' | 'onboarding' | 'connect';
  /**
   * Renders the 24x24 X dismiss control (YAR-154 AC8). Taken as a callback
   * rather than read from `useAuth()` because the three call sites source the
   * notice differently — the Hero reads AuthContext's `unsupportedNotice`,
   * StepSetupForwarding and Settings pass their own. Only a caller that owns
   * the visibility state can actually make the alert go away, so a caller
   * without one renders no X instead of a dead control.
   */
  onDismiss?: () => void;
}

function body(
  reason: AccountNoticeReason,
  email: string | undefined,
  variant: 'default' | 'onboarding' | 'connect',
  signedInEmail?: string,
): string {
  if (reason === 'account_mismatch') {
    // Name both addresses. The generic version of this ("wrong account") sent
    // testers back to Google to pick the same wrong one again, because with
    // two Google sessions open there is no way to tell which is which.
    const picked = email ? ` You picked ${email}.` : '';
    const signedIn = signedInEmail
      ? `You're signed in to Yaruta as ${signedInEmail}, and that's the mailbox Yaruta connects.`
      : "Yaruta connects the mailbox you're signed in with.";
    return `${signedIn}${picked} Connect again and choose that account in Google's picker.`;
  }
  if (reason === 'missing_scopes') {
    // Onboarding uses YAR-160 AC6's approved error-state sentence. Settings
    // keeps the longer wording: outside the connect flow the parent has no
    // trust bullets in front of them, so naming the two capabilities is the
    // only place that explains WHICH permissions Yaruta is asking for.
    if (variant !== 'default') {
      return ONBOARDING_COPY.gmail.permissionsDeclined;
    }
    return "Yaruta needs permission to read incoming mail and manage a Gmail label. Some permissions weren't granted — reconnect and allow all requested permissions to continue.";
  }
  if (variant !== 'default') {
    return ONBOARDING_COPY.welcome.personalGmailBody;
  }
  const who = email ? ` (${email})` : '';
  return `Yaruta works with personal Gmail accounts only. We can't use this Google Workspace / business account${who}. Use a personal Gmail account (@gmail.com) to continue.`;
}

export function UnsupportedAccountNotice({
  reason,
  email,
  signedInEmail,
  actionLabel,
  onAction,
  variant = 'default',
  onDismiss,
}: UnsupportedAccountNoticeProps) {
  return (
    // Alert-card pattern shared with InfoAlert and measured off Figma
    // 9672:111717: full width, 8px radius, 1px border, 12px padding all sides.
    // Border is `border/error/default-subtle` (#fde6e6), not the mid-tone
    // #c94040 fill colour — QA measured it on YAR-154.
    <div
      role="alert"
      className="flex w-full flex-col gap-3 rounded-lg border border-danger-border bg-danger-light p-3 text-left"
    >
      {/* Text column and the X are the two cross-axis items-start children of
          this row, 12px apart per Figma (the X box's top edge lines up with
          the card's content top, not with the title's optical center). */}
      <div className="flex items-start gap-3">
        {/* 12px between glyph and copy — Figma's `.Alerts` uses
            spacing/component/md there, the same as the card's padding and
            the gap to the X. It was 8px, which the blue default state
            (InfoAlert, already 12px) made visible as a text jump when the
            two swapped. */}
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {/* Hidden on the HERO card only: it is already red-on-red and the
              Hero has its own iconography, so a glyph read as a second
              alert (YAR-154). `default` (Settings) and `connect` (the
              Connect Gmail screen) both draw one.

              The Connect screen's glyph is the `i` (Info), not the `!`
              (CircleAlert): that is the mark Figma's `.Alerts` component
              carries on 7555:19777, and Kelvin confirmed it on YAR-372
              after QA flagged ours as "upside down". Settings keeps
              CircleAlert — nothing on that screen was reviewed. */}
          {variant === 'connect' ? (
            // 24px and top-aligned, no optical nudge: that is the `.Alerts`
            // icon box on Figma 7555:19777, and the blue default state this
            // replaces (StepSetupForwarding) draws its glyph the same way, so
            // the swap between the two states does not shift the text.
            // strokeWidth 1.5 is the `.Alerts` glyph on Figma; lucide's
            // default is 2 (QA, YAR-372, Sep 22).
            <Info
              size={24}
              strokeWidth={1.5}
              className="text-icon-error shrink-0"
              aria-hidden="true"
            />
          ) : variant !== 'onboarding' ? (
            <CircleAlert
              size={20}
              className="text-icon-error shrink-0 mt-0.5"
              aria-hidden="true"
            />
          ) : null}
          {variant === 'onboarding' ? (
            // Title 16px and body copy both in `text/error/default`
            // (#a82222) per Figma — they were 14px #2f2f2f.
            <div className="flex min-w-0 flex-col gap-1">
              <p className="text-base font-medium text-text-error leading-6">
                {reason === 'non_personal_domain'
                  ? ONBOARDING_COPY.welcome.personalGmailTitle
                  : reason === 'account_mismatch'
                    ? 'Wrong Google account'
                    : 'Reconnect Gmail'}
              </p>
              <p className="text-sm text-text-error leading-5">
                {body(reason, email, variant, signedInEmail)}
              </p>
            </div>
          ) : (
            <p
              className={`min-w-0 text-sm leading-5 ${
                variant === 'connect' ? 'text-text-error' : 'text-foreground'
              }`}
            >
              {body(reason, email, variant, signedInEmail)}
            </p>
          )}
        </div>

        {/* AC8 requires the hard stop to be DISMISSIBLE: without this the only
            way to clear it is another Google OAuth round trip, so a parent who
            reads it and decides not to retry keeps it pinned above the CTA
            cluster for the rest of the session. #5e5e5e (muted-foreground) per
            Figma — the X is neutral, not red. */}
        {onDismiss && (
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="shrink-0 cursor-pointer rounded-sm text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <X
              size={24}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
      {actionLabel && onAction && (
        <Button
          variant="primary"
          fullWidth
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
      {/* Points at the external early-access form, not the internal
          `/waitlist` route — that route bounces signed-out (and rejected)
          visitors back to `/`, so it read as a dead link (YAR-277). New tab
          so the user doesn't lose this notice.

          Hidden for `account_mismatch` AND `missing_scopes`: both users
          demonstrably HAVE a usable Gmail — one picked a different account,
          the other unchecked a permission box — so offering the waitlist
          reads as a rejection they haven't received. Only
          `non_personal_domain` is a real "you cannot use Yaruta with this
          account", which is what the waitlist is for.

          Hidden for the `onboarding` variant too (YAR-154): the Hero already
          renders the same waitlist CTA directly under the Continue button, so
          inside the card it was the same link twice on one screen. */}
      {reason === 'non_personal_domain' && variant !== 'onboarding' && (
        <a
          href={WAITLIST_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-semibold text-muted-foreground self-center py-1 px-1.5"
        >
          Don't have a personal Gmail? Join the waitlist
        </a>
      )}
    </div>
  );
}
